import clsx from "clsx";
import React, { useContext, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Button from "web/react/components/button/button";
import { IconButton } from "web/react/components/buttons/icon-button/icon-button";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { closeSidebar } from "web/redux/ducks/feed-sidebar";
import { gettext } from "web/script/modules/django-i18n";
import styles from "./filter-dialog.module.css";

interface FilterDialogProps {
    open: boolean;
    children?: React.ReactNode;
    isDisabled?: boolean;
    onApply?(): void;
    onReset?(): void;
    onClose?(): void;
}

interface FilterDialogContextInterface {
    title: string;
    setTitle(value: string): void;
    showBackButton?: boolean;
    setShowBackButton(value: boolean): void;
    onBack(): void;
    setOnBack(value: () => void): void;
}

export const FilterDialogContext = React.createContext<FilterDialogContextInterface>({
    title: "",
    setTitle: () => {},
    showBackButton: true,
    setShowBackButton: () => {},
    onBack: () => {},
    setOnBack: () => {},
});

interface FilterDialogHeaderProps {
    onDismiss(): void;
}

const FilterDialogHeader: React.FC<FilterDialogHeaderProps> = ({ onDismiss }) => {
    const { showBackButton, onBack, title } = useContext(FilterDialogContext);

    return (
        <header className={styles.header}>
            {showBackButton ? (
                <IconButton
                    className={styles.back}
                    icon={<SVGIcon className={styles.icon} name="arrow-left" />}
                    aria-label={gettext("general.back_btn_text")}
                    onClick={onBack}
                />
            ) : null}
            <h2 className={styles.title}>{title}</h2>
            <IconButton
                className={styles.close}
                icon={<SVGIcon name="close" />}
                aria-label={gettext("general.close")}
                onClick={onDismiss}
            />
        </header>
    );
};

export function FilterDialog({
    open,
    children,
    onApply,
    onReset,
    onClose,
    isDisabled,
}: FilterDialogProps): JSX.Element {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [title, setTitle] = useState<string>("");
    const [onBack, setOnBack] = useState<() => void>(() => () => {
        navigate("/");
    });
    const [showBackButton, setShowBackButton] = useState<boolean>();

    const value = useMemo(
        () => ({ title, onBack, showBackButton, setShowBackButton, setTitle, setOnBack }),
        [title, showBackButton, onBack]
    );

    const onDismiss = (): void => {
        dispatch(closeSidebar());
        navigate("/");
    };

    return (
        <FilterDialogContext.Provider value={value}>
            <div className={clsx(styles.dialog, { [styles.open]: open })}>
                <div className={styles.screen}>
                    <FilterDialogHeader
                        onDismiss={() => {
                            onDismiss();
                            onClose?.();
                        }}
                    />

                    <div className={styles.content}>{children}</div>

                    <div className={styles.buttons}>
                        <Button
                            onClick={() => {
                                onDismiss();
                                onReset?.();
                            }}
                            styleType="primary"
                            fullWidth={true}
                        >
                            {gettext("feed.filters.mobile_clear_all_btn_text")}
                        </Button>

                        <Button
                            onClick={() => {
                                onDismiss();
                                onApply?.();
                            }}
                            styleType="cta"
                            fullWidth={true}
                            disabled={isDisabled}
                        >
                            {gettext("feed.filters.apply_btn_text")}
                        </Button>
                    </div>
                </div>
            </div>
        </FilterDialogContext.Provider>
    );
}
